import {useEffect, useRef, useState} from "react";
import {
    ANNOUNCEMENT_SCHEDULES_STORAGE_KEY,
    ANNOUNCEMENT_SCHEDULES_UPDATED_EVENT,
    addBroadcastHistoryEntry,
    readAnnouncementSchedules,
    saveAnnouncementSchedule,
    syncAnnouncementsWithBackend,
} from "../utils/AnnouncementStorageUtils";
import {playAnnouncement} from "../utils/AnnouncementPlayer";

// Independent scheduler for Virtual Announcements. It mirrors the timing
// approach of BellScheduler (1s worker tick + lookback de-duplication) but
// operates on its own localStorage key so the bell schedule is never affected.

const PLAYED_SLOT_STORAGE_KEY = "announcement:last-played-slot";
const SCHEDULE_LOOKBACK_MS = 90 * 1000;
const PLAYED_SLOT_TTL_MS = 24 * 60 * 60 * 1000;

const getScheduleDate = (baseDate, time) => {
    // Accepts "H:MM AM/PM" (legacy) or "H:MM:SS AM/PM" (with seconds).
    const match = String(time || "").trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);

    if (!match) {
        return null;
    }

    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3] || "0");
    const ampm = match[4].toUpperCase();

    if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        Number.isNaN(seconds) ||
        hours < 1 ||
        hours > 12 ||
        minutes > 59 ||
        seconds > 59
    ) {
        return null;
    }

    if (ampm === "PM" && hours !== 12) {
        hours += 12;
    }
    if (ampm === "AM" && hours === 12) {
        hours = 0;
    }

    const scheduleDate = new Date(baseDate);
    scheduleDate.setHours(hours, minutes, seconds, 0);
    return scheduleDate;
};

const shouldPlayNow = (schedule, now) => {
    if (schedule.status !== "Y") {
        return null;
    }

    // Optional day-of-week filter. Empty / missing days => every day.
    if (Array.isArray(schedule.days) && schedule.days.length > 0) {
        if (!schedule.days.includes(now.getDay())) {
            return null;
        }
    }

    const scheduleDate = getScheduleDate(now, schedule.time);
    if (!scheduleDate) {
        return null;
    }

    const delayMs = now.getTime() - scheduleDate.getTime();
    return delayMs >= 0 && delayMs <= SCHEDULE_LOOKBACK_MS ? scheduleDate : null;
};

const getPlaybackSlot = (date, time, key) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}-${time}-${key}`;
};

const claimPlaybackSlot = (slot) => {
    const now = Date.now();
    const stored = localStorage.getItem(PLAYED_SLOT_STORAGE_KEY);
    let playedSlots = {};

    try {
        playedSlots = stored ? JSON.parse(stored) : {};
    } catch (e) {
        playedSlots = {};
    }

    if (playedSlots[slot]) {
        return false;
    }

    Object.keys(playedSlots).forEach((playedSlot) => {
        if (now - Number(playedSlots[playedSlot]) > PLAYED_SLOT_TTL_MS) {
            delete playedSlots[playedSlot];
        }
    });

    playedSlots[slot] = now;
    localStorage.setItem(PLAYED_SLOT_STORAGE_KEY, JSON.stringify(playedSlots));
    return true;
};

const AnnouncementScheduler = () => {
    const [schedules, setSchedules] = useState([]);
    const schedulesRef = useRef([]);
    const playedSlots = useRef({});

    useEffect(() => {
        schedulesRef.current = schedules;
    }, [schedules]);

    useEffect(() => {
        const load = (event) => {
            const data = event?.detail?.schedules || readAnnouncementSchedules();
            setSchedules(Array.isArray(data) ? data : []);
        };

        const handleStorageChange = (event) => {
            if (event.key === ANNOUNCEMENT_SCHEDULES_STORAGE_KEY) {
                load();
            }
        };

        load();
        // One-time reconcile with the backend: push local-only announcements up
        // (backward compat) and pull any the backend has that this device lacks.
        // setAnnouncementSchedules inside the sync dispatches the updated event,
        // which re-runs load() above, so no manual refresh is needed here.
        syncAnnouncementsWithBackend();
        window.addEventListener(ANNOUNCEMENT_SCHEDULES_UPDATED_EVENT, load);
        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener(ANNOUNCEMENT_SCHEDULES_UPDATED_EVENT, load);
            window.removeEventListener("storage", handleStorageChange);
        };
    }, []);

    useEffect(() => {
        const runAnnouncement = (schedule) => {
            playAnnouncement(schedule)
                .then(() => {
                    saveAnnouncementSchedule({id: schedule.id, lastPlayedAt: Date.now()});
                    addBroadcastHistoryEntry({
                        source: "scheduled",
                        scheduleId: schedule.id,
                        type: schedule.type || "text",
                        title: schedule.title || "Scheduled announcement",
                        text: schedule.text || "",
                        recordingId: schedule.recordingId || "",
                        recordingName: schedule.recordingName || "",
                        openingSound: schedule.openingSound || "none",
                        rate: schedule.rate,
                        pitch: schedule.pitch,
                        voiceName: schedule.voiceName || "",
                        lang: schedule.lang || "",
                        gender: schedule.gender || "",
                    });
                    // One-off announcements disable themselves after playing.
                    if (!schedule.repeat) {
                        saveAnnouncementSchedule({id: schedule.id, status: "N"});
                    }
                })
                .catch((err) => console.warn("Announcement playback failed:", err));
        };

        const evaluateSchedule = (dateValue = Date.now()) => {
            const now = new Date(dateValue);

            schedulesRef.current.forEach((schedule) => {
                const scheduleDate = shouldPlayNow(schedule, now);
                if (!scheduleDate) {
                    return;
                }

                const slot = getPlaybackSlot(scheduleDate, schedule.time, schedule.id);
                if (!playedSlots.current[slot] && claimPlaybackSlot(slot)) {
                    runAnnouncement(schedule);
                    playedSlots.current[slot] = true;
                    setTimeout(() => {
                        delete playedSlots.current[slot];
                    }, SCHEDULE_LOOKBACK_MS);
                }
            });
        };

        const publicUrl = process.env.PUBLIC_URL || "";
        const workerUrl = `${publicUrl.replace(/\/$/, "")}/bell-scheduler-worker.js`;
        let worker = null;
        let fallbackInterval = null;

        if (window.Worker) {
            try {
                worker = new Worker(workerUrl);
                worker.onmessage = (event) => {
                    if (event.data?.type === "tick") {
                        evaluateSchedule(event.data.now);
                    }
                };
                worker.onerror = () => {
                    worker?.terminate();
                    worker = null;
                    fallbackInterval = setInterval(() => evaluateSchedule(), 1000);
                };
                worker.postMessage({type: "start"});
            } catch (e) {
                fallbackInterval = setInterval(() => evaluateSchedule(), 1000);
            }
        } else {
            fallbackInterval = setInterval(() => evaluateSchedule(), 1000);
        }

        const handleVisibilityChange = () => evaluateSchedule();
        window.addEventListener("focus", handleVisibilityChange);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            worker?.postMessage({type: "stop"});
            worker?.terminate();
            clearInterval(fallbackInterval);
            window.removeEventListener("focus", handleVisibilityChange);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    return null;
};

export default AnnouncementScheduler;
